import { useMemo } from 'react';
import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query'; //-tech-debt-react-query;
import { moodleFetch } from './useMoodleEventBooking'; // reuse your existing moodleFetch()
import { CacheKey } from '../types/cache-keys';

export type MoodleLeaderCompletionRow = {
    email: string;
    first_name?: string;
    last_name?: string;

    course_id: number;
    course_fullname?: string;
    course_shortname?: string;

    category_id?: number;
    category_name?: string;

    completion_status?: string; // 'Completed'|'Not Completed' if returned
    date_completed_utc?: string;

    is_proctor?: string | boolean; // older endpoints used Yes/No; normalize in UI
};

export type MoodleLeaderFeedbackResponse = any; // your /feedback/by-user/ response is nested

export function useLeaderCourseCompletionsByManager(
    mgrEmail: string | null | undefined,
    params?: { course_id?: number; recursive?: boolean },
    options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
): UseQueryResult<any, Error> {
    const queryParams = useMemo(() => {
        if (!mgrEmail) return null;
        return {
            mgr_email: mgrEmail,
            recursive: params?.recursive ?? true,
            ...(params?.course_id ? { course_id: params.course_id } : {}),
        };
    }, [mgrEmail, params?.course_id, params?.recursive]);

    return useQuery<any, Error>({
      queryKey: [CacheKey.LeaderMoodleCompletionsByManager, mgrEmail, queryParams],
      queryFn: async () => {
            if (!queryParams) return null;
            return moodleFetch<any>('moodle/course/completions/by-manager/', queryParams);
        },
      ...{ enabled: !!mgrEmail, refetchOnWindowFocus: false, staleTime: 30_000, ...(options || {}) },
    });
}

export function useLeaderCourseCompletionsByUser(
    email: string | null | undefined,
    params?: { course_id?: number },
    options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
): UseQueryResult<any, Error> {
    const queryParams = useMemo(() => {
        if (!email) return null;
        return {
            email,
            ...(params?.course_id ? { course_id: params.course_id } : {}),
        };
    }, [email, params?.course_id]);

    return useQuery<any, Error>({
      queryKey: [CacheKey.LeaderMoodleCompletionsByUser, email, queryParams],
      queryFn: async () => {
            if (!queryParams) return null;
            return moodleFetch<any>('moodle/course/completions/by-user/', queryParams);
        },
      ...{ enabled: !!email, refetchOnWindowFocus: false, staleTime: 30_000, ...(options || {}) },
    });
}

export function useLeaderFeedbackByUser(
    email: string | null | undefined,
    params?: { days_past?: number; days_future?: number },
    options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
): UseQueryResult<any, Error> {
    const queryParams = useMemo(() => {
        if (!email) return null;
        return {
            email,
            days_past: params?.days_past ?? 365,
            days_future: params?.days_future ?? 30,
        };
    }, [email, params?.days_past, params?.days_future]);

    return useQuery<any, Error>({
      queryKey: [CacheKey.LeaderMoodleFeedbackByUser, email, queryParams],
      queryFn: async () => {
            if (!queryParams) return null;
            return moodleFetch<any>('moodle/feedback/by-user/', queryParams);
        },
      ...{ enabled: !!email, refetchOnWindowFocus: false, staleTime: 30_000, ...(options || {}) },
    });
}

/**
 * Bulk TTT status: POST body { emails: [...] }
 * Endpoint per your backend: /api/moodle/moodle/ttt/bulk-user-courses-certificate-status/
 */
export type TttBulkStatusResponse = Record<
    string,
    Array<{
        course_id: number;
        course_fullname: string;
        course_shortname: string;
        category_id: number;
        category_name: string;
        has_certificate: boolean;
        coursecertificate_id?: number | null;
        coursecertificate_name?: string | null;
    }>
>;

export function useTttBulkCertificateStatus(
    emails: string[] | null | undefined,
    options?: Omit<UseQueryOptions<TttBulkStatusResponse, Error>, 'queryKey' | 'queryFn'>
): UseQueryResult<TttBulkStatusResponse, Error> {
    const payload = useMemo(() => {
        const arr = (emails || []).map((e) => String(e).toLowerCase()).filter(Boolean);
        const dedup = Array.from(new Set(arr)).sort();
        return dedup.length ? { emails: dedup } : null;
    }, [emails]);

    return useQuery<TttBulkStatusResponse, Error>({
      queryKey: [CacheKey.LeaderTttBulkStatus, payload],
      queryFn: async () => {
            if (!payload) return {} as any;
            return moodleFetch<TttBulkStatusResponse>(
                'moodle/moodle/ttt/bulk-user-courses-certificate-status/',
                {},
                { method: 'POST', body: JSON.stringify(payload) }
            );
        },
      ...{
              enabled: !!payload,
              refetchOnWindowFocus: false,
              staleTime: 60_000,
              ...(options || {}),
          },
    });
}